/*
 * @providesModule GlobalEventEmitter
 *
 */

import {
  EmitterSubscription,
  NativeEventEmitter,
  NativeEventSubscription,
  NativeModules,
  Platform,
} from 'react-native';
import {ValidEventListener} from './EventDefinition';

const Listener = {
  remove: () => {},
};

const NativeEmitter =
  Platform.OS === 'ios'
    ? new NativeEventEmitter(NativeModules.SKZGlobalEventEmitter)
    : new NativeEventEmitter();

const eventsMap: Record<string, WeakMap<any, any>> = {};

/**
 * @param {T} eventType - a valid event name. Must be a key in the ValidEventListener type
 * @param {ValidEventListener[T]} listener - listener associated with the event. Type inferred from the ValidEventListener type
 * @return {EmitterSubscription | NativeEventSubscription} Subscription object with a `remove` method to unregister the listener
 * @example GlobalEmitter.addListener("NoArgEvent", () => console.log("emitted"))
 * @example GlobalEmitter.addListener("HasArgEvent", (arg) => console.log(`emitted with ${arg}`))
 * @example
 * GlobalEmitter.addListener("HasCallbackEvent", (arg, callback) => {
 *    console.log(`emitted with ${arg}`);
 *    callback();
 * })
 */
const addListener = <T extends keyof ValidEventListener>(
  eventType: T,
  listener: ValidEventListener[T]
): EmitterSubscription | NativeEventSubscription => {
  if (Platform.OS !== 'web') {
    return NativeEmitter.addListener(eventType, listener as any);
  } else {
    const handler = (e: any) => {
      const args = e.detail || []; // Cast CustomEvent.detail as args to mimic native events
      (listener as any)(...args);
    };

    const eventMap = eventsMap[eventType] ?? new WeakMap();
    eventMap.set(listener, handler);
    eventsMap[eventType] = eventMap;

    // Add the event listener
    window.addEventListener(eventType, handler);

    // Return an object with a remove method
    return {
      remove: () => {
        window.removeEventListener(eventType, handler);
      },
    } as EmitterSubscription;
  }
};

// Function signature when listenerArgs are provided
function emit<T extends keyof ValidEventListener>(
  eventType: T,
  ...listenerArgs: Parameters<ValidEventListener[T]>
): void;

// Function signature when no listenerArgs are provided
function emit<T extends keyof ValidEventListener>(eventType: T): void;

/**
 * @param {T} eventType - a valid event name. Must be a key in the ValidEventListener type
 * @param {*} listenerArgs - argument type inferred from value `ValidEventListener[eventType]`
 * @example GlobalEmitter.emit("NoArgEvent")
 * @example GlobalEmitter.emit("HasArgEvent", true)
 * @example GlobalEmitter.emit("HasCallbackEvent", true, () => null)
 */
// Actual function implementation
function emit<T extends keyof ValidEventListener>(
  eventType: T,
  ...listenerArgs: Parameters<ValidEventListener[T]>
): void {
  if (Platform.OS === 'web') {
    window.dispatchEvent(new CustomEvent(eventType, {detail: listenerArgs}));
  } else if (listenerArgs && listenerArgs.length > 0) {
    NativeEmitter.emit(eventType, ...listenerArgs);
  } else {
    NativeEmitter.emit(eventType);
  }
}

const removeListener = <T extends keyof ValidEventListener>(
  eventType: T,
  listener: ValidEventListener[T]
): void | boolean => {
  if (Platform.OS === 'web') {
    const handlers = eventsMap[eventType];
    const handler = handlers?.get(listener);
    if (handler) {
      handlers?.delete(listener);
      window.removeEventListener(eventType, handler as any);
    }
  } else {
    const subscription = NativeEmitter.addListener(eventType, listener as any);
    subscription.remove();
  }
};

const GlobalEmitter = {
  addListener,
  emit,
  removeListener,
};

export default GlobalEmitter;
